import { TabPanel, Button, Icon } from '@wordpress/components';
import { __ } from '@wordpress/i18n';
import {
    DndContext,
    closestCenter,
    MouseSensor,
    TouchSensor,
    useSensor,
    useSensors,
} from '@dnd-kit/core';
import {
    arrayMove,
    SortableContext,
    verticalListSortingStrategy,
    useSortable,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { dragHandle } from '@wordpress/icons';
import CustomPanel from './field/CustomPanel';
import GeneralSettingsTab from './field/GeneralSettingsTab';
import AdvancedSettingsTab from './field/AdvancedSettingsTab';
import { useState } from '@wordpress/element';

const DraggableField = ({ field, index, handleFieldChange, handleRemoveField, conditionFieldOptions, isDragging, tabs }) => {
    const { id } = field;

    const {
        attributes,
        listeners,
        setNodeRef,
        transform,
        transition,
    } = useSortable({ id });

    const isOpenForPanel = isDragging ? false : (field.isOpen ?? false);

    const style = {
        transform: CSS.Transform.toString(transform),
        transition,
    };

    return (
        <div ref={setNodeRef} style={style}>
            <div className="voxycure-drag__panel">
                <CustomPanel
                    key={`${field.id}-${isDragging}`}
                    index={index}
                    label={field.label || __('New Field', 'voxycure-framework')}
                    isOpenDefault={isOpenForPanel}
                    onToggle={(newIsOpen) => handleFieldChange(index, { ...field, isOpen: newIsOpen })}
                    dragAttributes={attributes}
                    dragListeners={listeners}
                >
                    <TabPanel
                        className="field-settings-tabs"
                        activeClass="active-tab"
                        initialTabName="general"
                        tabs={[
                            { name: 'general', title: __('General', 'voxycure-framework') },
                            { name: 'advanced', title: __('Advanced', 'voxycure-framework') },
                        ]}
                    >
                        {(tab) => (
                            <>
                                {tab.name === 'general' && (
                                    <GeneralSettingsTab
                                        field={field}
                                        onChange={(updatedField) => handleFieldChange(index, updatedField)}
                                        onRemove={() => handleRemoveField(index)}
                                        tabs={tabs}
                                    />
                                )}
                                {tab.name === 'advanced' && (
                                    <AdvancedSettingsTab
                                        conditionalLogic={field.conditional_logic || [[]]}
                                        conditionFieldOptions={conditionFieldOptions.filter(
                                            (opt) => opt.value !== field.field_key
                                        )}
                                        onChange={(key, value) =>
                                            handleFieldChange(index, { ...field, [key]: value })
                                        }
                                    />
                                )}

                                {field.type === 'repeater' && (
                                    <div className="voxy-repeater-fields" style={{ marginLeft: '20px' }}>
                                        <h4>{__('Repeater Sub-Fields', 'voxycure-framework')}</h4>
                                        <FieldSettings
                                            fields={field.fields || []}
                                            onFieldsChange={(updatedSubFields) =>
                                                handleFieldChange(index, { ...field, fields: updatedSubFields })
                                            }
                                        />
                                    </div>
                                )}
                            </>
                        )}
                    </TabPanel>
                </CustomPanel>
            </div>
        </div>
    );
};

const FieldSettings = ({ fields, tabs = [], onFieldsChange }) => {
    const sensors = useSensors(useSensor(MouseSensor), useSensor(TouchSensor));
    const [isDragging, setIsDragging] = useState(false);

    const handleAddNewField = () => {
        onFieldsChange([
            ...fields,
            {
                id: `field-${Date.now()}-${Math.random()}`,
                label: '',
                name: '',
                type: 'text',
                default_value: '',
                required: false,
                options: '',
                fields: [],
                isOpen: true,
                tab: tabs.length === 0 ? '' : tabs[0]?.name,
            },
        ]);
    };

    const handleFieldChange = (index, updatedField) => {
        const updatedFields = [...fields];
        updatedFields[index] = updatedField;
        onFieldsChange(updatedFields);
    };

    const handleRemoveField = (index) => {
        const updatedFields = [...fields];
        updatedFields.splice(index, 1);
        onFieldsChange(updatedFields);
    };

    const conditionFieldOptions = fields.filter((f) => f.field_key).map((f) => ({
        label: f.label || f.field_key,
        value: f.field_key
    }));

    const handleDragStart = () => {
        const updatedFields = fields.map(field => ({ ...field, isOpen: false }));
        onFieldsChange(updatedFields);
        setIsDragging(true);
    };

    const handleDragEnd = (event) => {
        const { active, over } = event;

        if (!active || !over || active.id === over.id) {
            setIsDragging(false);
            return;
        }

        const oldIndex = fields.findIndex((f) => f.id === active.id);
        const newIndex = fields.findIndex((f) => f.id === over.id);
        const updatedFields = arrayMove(fields, oldIndex, newIndex);
        onFieldsChange(updatedFields);
        setIsDragging(false);
    };

    return (
        <>
            {fields.length === 0 ? (
                <div style={{ marginBottom: '1em', color: '#555' }}>
                    {__('No fields found.', 'voxycure-framework')}
                </div>
            ) : (
                <DndContext
                    sensors={sensors}
                    collisionDetection={closestCenter}
                    onDragStart={handleDragStart}
                    onDragEnd={handleDragEnd}
                >
                    <SortableContext
                        items={fields.map((f) => f.id)}
                        strategy={verticalListSortingStrategy}
                    >
                        {fields.map((field, index) => (
                            <DraggableField
                                key={field.id}
                                field={field}
                                index={index}
                                handleFieldChange={handleFieldChange}
                                handleRemoveField={handleRemoveField}
                                conditionFieldOptions={conditionFieldOptions}
                                isDragging={isDragging}
                                tabs={tabs}
                            />
                        ))}
                    </SortableContext>
                </DndContext>
            )}
            <Button isSecondary onClick={handleAddNewField} style={{ marginTop: '8px' }}>
                {__('Add Field', 'voxycure-framework')}
            </Button>
        </>
    );
};

export default FieldSettings;
